package com.arenaproplay.admin.domain.admin

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/** ALL is only offered where the backend supports it (API stats). */
enum class Period(val code: String) { DAY("day"), WEEK("week"), MONTH("month"), YEAR("year"), ALL("all") }

@Serializable
data class KpiDelta(
    @SerialName("current") val current: Double,
    @SerialName("previous") val previous: Double
) {
    val absoluteDelta: Double get() = current - previous
    val percentDelta: Double? get() = if (previous == 0.0) null else (current - previous) / previous * 100.0
}

@Serializable
data class OverviewKpis(
    @SerialName("totalUsers") val totalUsers: KpiDelta,
    @SerialName("activeUsers") val activeUsers: KpiDelta,
    @SerialName("totalRevenue") val totalRevenue: KpiDelta,
    @SerialName("totalBookings") val totalBookings: KpiDelta,
    @SerialName("currency") val currency: String = "UAH"
)

/** ISO-8601 instant analytics count from, or null = full history. */
@Serializable
data class AnalyticsBaseline(
    @SerialName("baseline") val baseline: String? = null,
)

@Serializable
data class SubscriptionKpis(
    @SerialName("activeNow") val activeNow: Long,
    @SerialName("activations") val activations: KpiDelta,
    @SerialName("deactivations") val deactivations: KpiDelta,
    @SerialName("revenue") val revenue: KpiDelta,
    @SerialName("byProvider") val byProvider: List<CategoryShare>,
    @SerialName("byStatus") val byStatus: List<CategoryShare>,
    @SerialName("currency") val currency: String = "UAH",
)

enum class SubscriptionStatusFilter(val code: String) {
    ALL("all"),
    ACTIVE("active"),
    CANCELED("canceled"),
    EXPIRED("expired"),
}

enum class PlatformFilter(val code: String, val label: String) {
    ALL("all", "Все"), WEB("web", "Веб"), APP("app", "Приложение");
    /** query-param value: null для ALL */
    val param: String? get() = if (this == ALL) null else code
}

@Serializable
data class TimePoint(
    @SerialName("label") val label: String,
    @SerialName("value") val value: Double
)

@Serializable
data class CategorizedTimeSeries(
    @SerialName("labels") val labels: List<String>,
    @SerialName("series") val series: Map<String, List<Double>>
)

@Serializable
data class CategoryShare(
    @SerialName("name") val name: String,
    @SerialName("value") val value: Double
)

@Serializable
data class ActivityItem(
    @SerialName("id") val id: String,
    @SerialName("timestamp") val timestamp: String,
    @SerialName("actor") val actor: String,
    @SerialName("action") val action: String,
    @SerialName("target") val target: String? = null,
    @SerialName("kind") val kind: String
)

@Serializable
data class UserRow(
    @SerialName("id") val id: String,
    @SerialName("name") val name: String,
    @SerialName("email") val email: String,
    @SerialName("role") val role: String,
    @SerialName("createdAt") val createdAt: String,
    @SerialName("lastActive") val lastActive: String,
    @SerialName("status") val status: String,
    @SerialName("platforms") val platforms: List<String> = emptyList(),
    @SerialName("proActive") val proActive: Boolean = false,
    /** "ADMIN" for admin grants, else the store/provider name. */
    @SerialName("proSource") val proSource: String? = null,
    @SerialName("proUntil") val proUntil: String? = null,
    @SerialName("country") val country: String? = null,
    @SerialName("city") val city: String? = null,
)

@Serializable
data class EventRow(
    @SerialName("id") val id: String,
    @SerialName("title") val title: String,
    @SerialName("sport") val sport: String,
    @SerialName("participants") val participants: Int,
    @SerialName("maxParticipants") val maxParticipants: Int,
    @SerialName("organizer") val organizer: String,
    @SerialName("startAt") val startAt: String,
    @SerialName("status") val status: String,
    @SerialName("city") val city: String? = null,
    @SerialName("isPro") val isPro: Boolean = false,
)

@Serializable
data class SubscriptionRow(
    @SerialName("userId") val userId: String,
    @SerialName("user") val user: String,
    @SerialName("email") val email: String,
    @SerialName("status") val status: String,
    /** Access is granted right now (CANCELED can still be active until the period ends). */
    @SerialName("active") val active: Boolean,
    @SerialName("provider") val provider: String,
    @SerialName("purchasedAt") val purchasedAt: String,
    @SerialName("currentPeriodEnd") val currentPeriodEnd: String,
    @SerialName("cancelAtPeriodEnd") val cancelAtPeriodEnd: Boolean = false,
)

@Serializable
data class PageResult<T>(
    @SerialName("items") val items: List<T>,
    @SerialName("page") val page: Int,
    @SerialName("pageSize") val pageSize: Int,
    @SerialName("total") val total: Int
)

@Serializable
data class ApiRequestStats(
    @SerialName("method") val method: String,
    @SerialName("path") val path: String,
    @SerialName("count") val count: Long,
    @SerialName("avgLatencyMs") val avgLatencyMs: Double,
    @SerialName("maxLatencyMs") val maxLatencyMs: Int,
    @SerialName("errorCount") val errorCount: Long,
)

@Serializable
data class ApiPlatformBreakdown(
    @SerialName("method") val method: String,
    @SerialName("path") val path: String,
    @SerialName("byPlatform") val byPlatform: Map<String, Long>,
)

@Serializable
data class ApiErrorRow(
    @SerialName("method") val method: String,
    @SerialName("path") val path: String,
    @SerialName("status") val status: Int,
    @SerialName("count") val count: Long,
)

@Serializable
data class RetentionCohort(
    @SerialName("cohortStart") val cohortStart: String,
    @SerialName("cohortSize") val cohortSize: Int,
    @SerialName("d1") val d1: Double? = null,
    @SerialName("d7") val d7: Double? = null,
    @SerialName("d30") val d30: Double? = null,
)

@Serializable
data class FunnelStep(
    @SerialName("key") val key: String,
    @SerialName("label") val label: String,
    @SerialName("userCount") val userCount: Long,
)

@Serializable
data class CityEventCount(
    @SerialName("city") val city: String,
    @SerialName("eventCount") val eventCount: Long,
)

@Serializable
data class EventLifecycleStats(
    @SerialName("totalEvents") val totalEvents: Long,
    @SerialName("statusBreakdown") val statusBreakdown: Map<String, Long>,
    @SerialName("avgLeadTimeHours") val avgLeadTimeHours: Double,
    @SerialName("avgFillRate") val avgFillRate: Double,
    @SerialName("cancellationRate") val cancellationRate: Double,
    @SerialName("completionRate") val completionRate: Double,
)

@Serializable
data class OnboardingStep(
    @SerialName("key") val key: String,
    @SerialName("label") val label: String,
    @SerialName("completed") val completed: Long,
)

@Serializable
data class OnboardingStats(
    @SerialName("totalActiveUsers") val totalActiveUsers: Long,
    @SerialName("steps") val steps: List<OnboardingStep>,
)

@Serializable
data class SportEventSize(
    @SerialName("sport") val sport: String,
    @SerialName("eventCount") val eventCount: Long,
    @SerialName("minPlayers") val minPlayers: Int,
    @SerialName("maxPlayers") val maxPlayers: Int,
    @SerialName("avgPlayers") val avgPlayers: Double,
)

@Serializable
data class AdminStoriesPage(
    @SerialName("items") val items: List<AdminStory>,
    @SerialName("page") val page: Int,
    @SerialName("size") val size: Int,
    @SerialName("totalItems") val totalItems: Long,
    @SerialName("totalPages") val totalPages: Int,
)

/** An active user story as listed by GET /admin/social/stories. */
@Serializable
data class AdminStory(
    @SerialName("id") val id: String,
    @SerialName("authorId") val authorId: String,
    @SerialName("authorName") val authorName: String,
    @SerialName("authorAvatarUrl") val authorAvatarUrl: String? = null,
    @SerialName("imageUrl") val imageUrl: String,
    @SerialName("createdAt") val createdAt: String,
    @SerialName("expiresAt") val expiresAt: String,
    @SerialName("likesCount") val likesCount: Long = 0,
    @SerialName("viewsCount") val viewsCount: Long = 0,
)

@Serializable
data class AuditEntry(
    @SerialName("id") val id: String,
    @SerialName("adminId") val adminId: String,
    @SerialName("action") val action: String,
    @SerialName("targetKind") val targetKind: String? = null,
    @SerialName("targetId") val targetId: String? = null,
    @SerialName("metadata") val metadata: String? = null,
    @SerialName("ip") val ip: String? = null,
    @SerialName("createdAt") val createdAt: String,
)

@Serializable
data class SupportMessageRow(
    @SerialName("id") val id: String,
    @SerialName("email") val email: String,
    @SerialName("message") val message: String,
    @SerialName("subject") val subject: String? = null,
    @SerialName("attachmentUrl") val attachmentUrl: String? = null,
    @SerialName("userId") val userId: String? = null,
    @SerialName("createdAt") val createdAt: String,
)

@Serializable
data class SupportMessagesPage(
    @SerialName("total") val total: Long,
    @SerialName("items") val items: List<SupportMessageRow>,
)

/** Ответ POST /v1/admin/moderation/users/{userId}/avatar/remove */
@Serializable
data class AvatarRemoveResult(
    @SerialName("removed") val removed: Boolean,
)

/** Ответ GET /v1/admin/moderation/users/{userId}/photos */
@Serializable
data class UserPhotos(
    @SerialName("avatarUrl") val avatarUrl: String? = null,
    @SerialName("cards") val cards: List<UserCardPhoto> = emptyList(),
)

@Serializable
data class UserCardPhoto(
    @SerialName("cardId") val cardId: String,
    @SerialName("avatarUrl") val avatarUrl: String? = null,
    @SerialName("templateName") val templateName: String,
)

/** Полный профиль для страницы «Пипл» — зеркало backend AdminPersonDto. */
@Serializable
data class AdminPerson(
    @SerialName("id") val id: String,
    @SerialName("name") val name: String,
    @SerialName("avatarUrl") val avatarUrl: String,
    @SerialName("age") val age: Int,
    @SerialName("city") val city: String,
    @SerialName("country") val country: String? = null,
    @SerialName("interests") val interests: List<String> = emptyList(),
    @SerialName("lastActive") val lastActive: String? = null,
    // Поля карточки игрока (фича feature/people-cards, добавляются бэкендом)
    @SerialName("cardAvatarUrl") val cardAvatarUrl: String? = null,
    @SerialName("cardAssetsBasePath") val cardAssetsBasePath: String? = null,
    @SerialName("cardTextColor") val cardTextColor: String? = null,
    @SerialName("cardImageUrl") val cardImageUrl: String? = null,
)

@Serializable
data class AppAnnouncementRow(
    @SerialName("id") val id: String,
    @SerialName("active") val active: Boolean,
    @SerialName("platforms") val platforms: List<String>,
    @SerialName("title") val title: Map<String, String>,
    @SerialName("body") val body: Map<String, String>,
    @SerialName("imageUrl") val imageUrl: String? = null,
    @SerialName("ctaUrl") val ctaUrl: String? = null,
    @SerialName("startsAt") val startsAt: String? = null,
    @SerialName("endsAt") val endsAt: String? = null,
    @SerialName("createdAt") val createdAt: String,
)

@Serializable
data class SaveAppAnnouncementPayload(
    @SerialName("active") val active: Boolean = true,
    @SerialName("platforms") val platforms: List<String> = listOf("android", "ios"),
    @SerialName("title") val title: Map<String, String>,
    @SerialName("body") val body: Map<String, String>,
    @SerialName("imageUrl") val imageUrl: String? = null,
    @SerialName("ctaUrl") val ctaUrl: String? = null,
    @SerialName("startsAt") val startsAt: String? = null,
    @SerialName("endsAt") val endsAt: String? = null,
)

@Serializable
data class AppUpdatePolicyRow(
    @SerialName("minSupportedVersion") val minSupportedVersion: Int,
    @SerialName("latestVersion") val latestVersion: Int,
    @SerialName("storeUrl") val storeUrl: String,
)

@Serializable
data class AppUpdatePolicyMap(
    @SerialName("platforms") val platforms: Map<String, AppUpdatePolicyRow> = emptyMap(),
)

// ── Arena News moderation ────────────────────────────────────────────────────

@Serializable
data class NewsArticleRow(
    val id: String,
    val title: String,
    val summary: String = "",
    val body: String = "",
    val coverImage: String = "",
    val sport: String = "football",
    val type: String = "NEWS",
    val status: String = "DRAFT",
    val sourceName: String = "",
    val sourceUrl: String = "",
    val locale: String = "en",
    val hashtags: List<String> = emptyList(),
    val viewsCount: Int = 0,
    /** AI-scored editorial importance 1-10 (5 = neutral). */
    val importance: Int = 5,
    val publishedAt: String? = null,
    /** 1..10 — слот в топе «Самые обсуждаемые»; null = не в топе. */
    val featuredRank: Int? = null,
    val match: NewsMatchLink? = null,
    val createdAt: String = "",
    val updatedAt: String = "",
)

@Serializable
data class NewsArticlePage(
    val items: List<NewsArticleRow>,
    val page: Int,
    val size: Int,
    val totalItems: Long,
    val totalPages: Int,
)

@Serializable
data class SaveNewsArticlePayload(
    val title: String,
    val summary: String = "",
    val body: String = "",
    val coverImage: String = "",
    val sport: String = "football",
    val type: String = "NEWS",
    val sourceName: String = "",
    val sourceUrl: String = "",
    val locale: String = "en",
    val hashtags: List<String> = emptyList(),
)

@Serializable
data class NewsStatusPayload(val status: String, val notify: Boolean = false)

@Serializable
data class NewsFeaturedPayload(
    /** 1..10 — слот в топе; null убирает из топа. */
    val rank: Int? = null,
)

/** Привязанный матч: снапшот для карточки + контекст для страницы матча. */
@Serializable
data class NewsMatchLink(
    val id: String,
    val sport: String = "football",
    /** ISO-8601 UTC kickoff. */
    val utcDate: String = "",
    val competitionCode: String = "",
    val competitionName: String = "",
    val homeTeam: String = "",
    val awayTeam: String = "",
    val homeCrest: String = "",
    val awayCrest: String = "",
)

@Serializable
data class SetNewsMatchLinkPayload(val match: NewsMatchLink? = null)

@Serializable
data class ReorderFeaturedPayload(
    /** id статей в порядке показа (слот 1 — первым); не вошедшие убираются. */
    val ids: List<String> = emptyList(),
)

/** Cover options: the RSS press photo is admin PREVIEW ONLY (publisher
 *  licensing), the Pexels stock list is safe to publish. */
@Serializable
data class NewsImageCandidates(
    val sourcePreview: String? = null,
    val stock: List<String> = emptyList(),
)

@Serializable
data class NewsRescoreReport(
    /** How many drafts were queued for background re-scoring. */
    val queued: String = "0",
)

@Serializable
data class NewsIngestReport(
    val scanned: String = "0",
    val created: String = "0",
    val rewritten: String = "0",
)

// ── Event templates ──────────────────────────────────────────────────────

/** Payload зеркалит backend EventTemplatePayload — все поля с @SerialName. */
@Serializable
data class AdminEventTemplatePayload(
    @SerialName("title") val title: String,
    @SerialName("sportId") val sportId: String,
    @SerialName("sportName") val sportName: String,
    @SerialName("customSportName") val customSportName: String? = null,
    @SerialName("description") val description: String? = null,
    @SerialName("privacy") val privacy: String,
    @SerialName("format") val format: Int? = null,
    @SerialName("teamsCount") val teamsCount: Int? = null,
    @SerialName("maxPlayers") val maxPlayers: Int? = null,
    @SerialName("cost") val cost: String,
    @SerialName("imageUrl") val imageUrl: String? = null,
    @SerialName("latitude") val latitude: String,
    @SerialName("longitude") val longitude: String,
    @SerialName("address") val address: String,
    @SerialName("city") val city: String? = null,
    @SerialName("isPro") val isPro: Boolean = false,
    @SerialName("teams") val teams: List<AdminTemplateTeam> = emptyList(),
    /** Confirmed-участники без команды (напр. теннис — команд нет вовсе); организатор не входит. */
    @SerialName("participants") val participants: List<AdminTemplateTeamMember> = emptyList(),
    /** Организатор исходного ивента — ивент из шаблона создаётся от его имени. Null у старых шаблонов. */
    @SerialName("organizerId") val organizerId: String? = null,
    @SerialName("organizerUsername") val organizerUsername: String? = null,
)

@Serializable
data class AdminTemplateTeam(
    @SerialName("name") val name: String,
    @SerialName("color") val color: String? = null,
    @SerialName("members") val members: List<AdminTemplateTeamMember> = emptyList(),
)

@Serializable
data class AdminTemplateTeamMember(
    @SerialName("userId") val userId: String,
    @SerialName("username") val username: String,
)

/** Ответ POST /v1/admin/events/{id}/apply-template/{templateId}. */
@Serializable
data class ApplyTemplateResult(
    @SerialName("teamsCreated") val teamsCreated: Int = 0,
    @SerialName("playersAdded") val playersAdded: Int = 0,
    @SerialName("playersSkipped") val playersSkipped: Int = 0,
)

/** Тело POST /v1/admin/event-templates/{id}/create-event. */
@Serializable
data class CreateEventFromTemplatePayload(
    @SerialName("dateTime") val dateTime: String,
    @SerialName("endDateTime") val endDateTime: String? = null,
)

/** Ответ POST /v1/admin/event-templates/{id}/create-event. */
@Serializable
data class CreateEventFromTemplateResult(
    @SerialName("eventId") val eventId: String,
    @SerialName("teamsCreated") val teamsCreated: Int = 0,
    @SerialName("playersAdded") val playersAdded: Int = 0,
    @SerialName("playersSkipped") val playersSkipped: Int = 0,
    /** false = у шаблона не было организатора (или юзер удалён) — создателем стал админ. */
    @SerialName("organizerApplied") val organizerApplied: Boolean = false,
)

@Serializable
data class AdminEventTemplate(
    @SerialName("id") val id: String,
    @SerialName("name") val name: String,
    @SerialName("payload") val payload: AdminEventTemplatePayload,
    @SerialName("createdAt") val createdAt: String,
)

@Serializable
data class SaveEventTemplatePayload(
    @SerialName("name") val name: String,
)

/** Тело POST /v1/events — зеркалит backend CreateEventRequest (без chatId). */
@Serializable
data class CreateEventPayload(
    @SerialName("title") val title: String,
    @SerialName("sportId") val sportId: String,
    @SerialName("customSportName") val customSportName: String? = null,
    @SerialName("description") val description: String? = null,
    @SerialName("privacy") val privacy: String = "PUBLIC",
    @SerialName("format") val format: Int? = null,
    @SerialName("teamsCount") val teamsCount: Int? = null,
    @SerialName("maxPlayers") val maxPlayers: Int? = null,
    @SerialName("cost") val cost: String = "0.00",
    @SerialName("imageUrl") val imageUrl: String? = null,
    @SerialName("dateTime") val dateTime: String,
    @SerialName("endDateTime") val endDateTime: String? = null,
    @SerialName("latitude") val latitude: String,
    @SerialName("longitude") val longitude: String,
    @SerialName("address") val address: String,
    @SerialName("city") val city: String? = null,
    @SerialName("isPro") val isPro: Boolean = false,
)

// ── Event management (detail page /events/{id}) ─────────────────────────

/** Subset of backend EventDetailDto returned by GET /v1/admin/events/{id}. */
@Serializable
data class AdminEventDetail(
    @SerialName("id") val id: String,
    @SerialName("title") val title: String,
    @SerialName("sportId") val sportId: String,
    @SerialName("sportName") val sportName: String,
    @SerialName("customSportName") val customSportName: String? = null,
    @SerialName("status") val status: String,
    @SerialName("isPro") val isPro: Boolean = false,
    @SerialName("maxPlayers") val maxPlayers: Int? = null,
    @SerialName("dateTime") val dateTime: String,
    @SerialName("address") val address: String,
    @SerialName("city") val city: String? = null,
    @SerialName("teams") val teams: List<AdminEventTeam> = emptyList(),
    @SerialName("participants") val participants: List<AdminEventParticipant> = emptyList(),
)

@Serializable
data class AdminEventTeam(
    @SerialName("id") val id: String,
    @SerialName("name") val name: String,
    @SerialName("color") val color: String? = null,
    @SerialName("score") val score: Int = 0,
)

@Serializable
data class AdminEventParticipant(
    @SerialName("id") val id: String,
    @SerialName("userId") val userId: String,
    @SerialName("username") val username: String,
    @SerialName("fullName") val fullName: String? = null,
    @SerialName("avatarUrl") val avatarUrl: String? = null,
    @SerialName("status") val status: String,
    @SerialName("isAdmin") val isAdmin: Boolean = false,
    @SerialName("eventTeamId") val eventTeamId: String? = null,
)

/** Subset of GET /v1/events/{id}/stats — PLAYER stat types + current values. */
@Serializable
data class AdminEventStats(
    @SerialName("statTypes") val statTypes: List<AdminSportStatType> = emptyList(),
    @SerialName("playerStats") val playerStats: List<AdminEventPlayerStat> = emptyList(),
)

@Serializable
data class AdminSportStatType(
    @SerialName("id") val id: String,
    @SerialName("name") val name: String,
    @SerialName("displayKey") val displayKey: String,
    @SerialName("displayOrder") val displayOrder: Int,
    @SerialName("scope") val scope: String = "PLAYER",
)

@Serializable
data class AdminEventPlayerStat(
    @SerialName("userId") val userId: String,
    @SerialName("statTypeId") val statTypeId: String,
    @SerialName("value") val value: Int,
)

/** Результат GET /v1/admin/users/search?q= — для добавления игроков в команду. */
@Serializable
data class AdminUserSearchResult(
    @SerialName("id") val id: String,
    @SerialName("username") val username: String,
    @SerialName("fullName") val fullName: String? = null,
    @SerialName("avatarUrl") val avatarUrl: String? = null,
)

@Serializable
data class AddEventParticipantPayload(
    @SerialName("userId") val userId: String,
    @SerialName("eventTeamId") val eventTeamId: String? = null,
)

@Serializable
data class AddEventTeamPayload(
    @SerialName("name") val name: String,
    @SerialName("color") val color: String? = null,
)

@Serializable
data class AssignEventTeamPayload(@SerialName("eventTeamId") val eventTeamId: String)

@Serializable
data class UpdateEventStatusPayload(@SerialName("status") val status: String)

@Serializable
data class SetTeamScorePayload(@SerialName("score") val score: Int)

@Serializable
data class EventStatEntryPayload(
    @SerialName("statTypeId") val statTypeId: String,
    @SerialName("value") val value: Int,
)

@Serializable
data class SetPlayerStatsPayload(@SerialName("stats") val stats: List<EventStatEntryPayload>)
